import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
  aiHubListDataGridRowBorderSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import type { ImplementationRecord } from "../../../services/implementationRecordService";
import AiHubNoRowsOverlay from "../AiHubNoRowsOverlay";
import AiHubTableToolbar from "../AiHubTableToolbar";
import { markdownToPlainText } from "../../../utils/markdownToPlainText";
import {
  displayOrDash,
  formatAssessmentRecordItems,
  formatAssessmentSourceLabel,
  formatImpactLevelCell,
  formatImpactYn,
  formatImpactedItemsLabel,
  formatReferenceItemsLabel,
} from "./implementationRecordView";

type ImplementationRecordTableProps = Readonly<{
  rows: ImplementationRecord[];
  rowCount: number;
  loading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onViewRecord: (record: ImplementationRecord) => void;
}>;

function TextCell({ value }: { value: string }) {
  const isEmpty = value === "-";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      <Typography
        noWrap
        title={isEmpty ? undefined : value}
        sx={{
          fontSize: 13,
          color: isEmpty ? "#8c8c8c" : "#101828",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function dashCell(params: GridRenderCellParams<ImplementationRecord, string>) {
  return <TextCell value={displayOrDash(params.value)} />;
}

export default function ImplementationRecordTable({
  rows,
  rowCount,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  onViewRecord,
}: ImplementationRecordTableProps) {
  const theme = useTheme();
  const panelScrollTopRef = useRef(0);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const toolbarSlot = useCallback(
    () => <AiHubTableToolbar title="Implementation" showExport={false} />,
    [],
  );

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={panelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const columns = useMemo<GridColDef<ImplementationRecord>[]>(
    () => [
      {
        field: "id",
        headerName: "Assmt. ID",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => <TextCell value={displayOrDash(row.id)} />,
      },
      {
        field: "assessmentSource",
        headerName: "Assessment Source",
        width: 200,
        sortable: false,
        valueGetter: (_value, row) =>
          formatAssessmentSourceLabel(row.assessmentSource),
        renderCell: dashCell,
      },
      {
        field: "planId",
        headerName: "Plan ID",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => <TextCell value={displayOrDash(row.planId)} />,
      },
      {
        field: "assessmentName",
        headerName: "Assessment Name",
        minWidth: 200,
        width: 220,
        sortable: false,
        renderCell: ({ row }) => (
          <TextCell value={displayOrDash(row.assessmentName)} />
        ),
      },
      {
        field: "impactedItems",
        headerName: "Impacted Items & ID",
        minWidth: 200,
        width: 220,
        sortable: false,
        valueGetter: (_value, row) =>
          formatImpactedItemsLabel(row.impactedObject),
        renderCell: dashCell,
      },
      {
        field: "referenceItems",
        headerName: "Reference Items & ID",
        minWidth: 220,
        width: 240,
        sortable: false,
        valueGetter: (_value, row) =>
          formatReferenceItemsLabel(row.referenceObject),
        renderCell: dashCell,
      },
      {
        field: "assessmentRecordItems",
        headerName: "Update Assessment Record (if impact = Y)",
        headerClassName: "assessment-record-items-header",
        minWidth: 200,
        width: 220,
        sortable: false,
        valueGetter: (_value, row) =>
          formatAssessmentRecordItems(row.assessmentRecordItems),
        renderCell: dashCell,
      },
      {
        field: "assessorName",
        headerName: "Assessor",
        width: 120,
        sortable: false,
        valueGetter: (_value, row) =>
          displayOrDash(row.assessorName || row.assessor),
        renderCell: dashCell,
      },
      {
        field: "impactYn",
        headerName: "Impact (Y/N)",
        width: 110,
        sortable: false,
        valueGetter: (_value, row) => formatImpactYn(row.impacted),
        renderCell: dashCell,
      },
      {
        field: "impactedLevel",
        headerName: "Impact Level",
        width: 120,
        sortable: false,
        valueGetter: (_value, row) =>
          formatImpactLevelCell(row.impacted, row.impactedLevel),
        renderCell: dashCell,
      },
      {
        field: "assessmentSummary",
        headerName: "Assessment Summary",
        minWidth: 200,
        width: 220,
        sortable: false,
        valueGetter: (_value, row) =>
          displayOrDash(markdownToPlainText(row.assessmentSummary)),
        renderCell: dashCell,
      },
      {
        field: "detailedAnalysis",
        headerName: "Detailed Analysis",
        minWidth: 180,
        width: 200,
        sortable: false,
        valueGetter: (_value, row) =>
          displayOrDash(markdownToPlainText(row.detailedAnalysis)),
        renderCell: dashCell,
      },
      {
        field: "recommendation",
        headerName: "Recommendation",
        minWidth: 160,
        width: 180,
        sortable: false,
        valueGetter: (_value, row) =>
          displayOrDash(markdownToPlainText(row.recommendation)),
        renderCell: dashCell,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 88,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <IconButton
              aria-label={`View assessment ${row.id}`}
              size="small"
              sx={{ p: 0.75 }}
              onClick={(event) => {
                event.stopPropagation();
                onViewRecord(row);
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "#A6A6A6" }} />
            </IconButton>
          </Box>
        ),
      },
    ],
    [onViewRecord],
  );

  const isEmpty = rows.length === 0 && !loading;

  return (
    <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={56}
        columnHeaderHeight={64}
        loading={loading}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        disableColumnFilter
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        initialState={{
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        slots={{
          toolbar: toolbarSlot,
          noRowsOverlay: () => <AiHubNoRowsOverlay hasScopeSelection />,
          columnsManagement: columnsManagementSlot,
        }}
        sx={
          [
            getRegulationsListDataGridSx(theme.palette.primary.main),
            aiHubListDataGridRowBorderSx,
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              minHeight: 400,
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "auto",
              },
              "& .assessment-record-items-header": {
                alignItems: "center",
                "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitle":
                  {
                    whiteSpace: "normal",
                    lineHeight: 1.25,
                    overflow: "visible",
                    textOverflow: "clip",
                  },
              },
            },
          ] as SxProps<Theme>
        }
      />
    </Box>
  );
}
